import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { Box, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DriveFileRenameOutline, VisibilityOutlined } from "@mui/icons-material";
import { FileSignature } from "lucide-react";
import { ProviderData } from "../ivr/IVRForm";
import CustomTable from "../../common/CustomTable";
import { Column, setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { previewPrescriptionPDF } from "../../../redux/slice/order/OrderSlice";
import PDFViewer from "../../common/pdf/PDFViewer";
import SigantureSubmitPopup from "./SignatureSubmitPopup";
import {
  getOrderDetailsForProvider,
  saveProviderSignAgainstOrder,
  useProviderNotificationData,
} from "../../../redux/slice/providerNotifications/ProviderNotificationSlice";
import { SUCCESS_CODE } from "../../../service/ServiceConstants";
import { useModalStyles } from "../../common/useModalStyles";
import { FieldLabel } from "../../../utils/Utility";

interface ProductItemProps {
  open: boolean;
  onClose: () => void;
  orderData?: any;
  addProductItem?: (productItem: any) => void;
  productItemDetails?: any;
  isEdit?: boolean;
  practiceName?: string;
  selectedProviderData?: ProviderData[];
  addSignature?: (signature: any) => void;
  from?: string;
  authorizedSigner?: boolean;
  authorizedSignerName?: any;
}

const SignOrderPopup: React.FC<ProductItemProps> = ({
  open,
  onClose,
  orderData,
  addProductItem,
  productItemDetails,
  isEdit,
  practiceName,
  selectedProviderData,
  addSignature,
  from,
  authorizedSigner = true,
  authorizedSignerName,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    closeIconSx,
    bodySx,
    footerSx,
    primaryBtnSx,
    cancelBtnSx,
    backBtnSx,
  } = useModalStyles();
  const [showPDF, setShowPDF] = useState<{ isShow: boolean; content: string; fileName: string }>({
    isShow: false,
    content: "",
    fileName: "",
  });
  const [showSignatureSubmitPopup, setShowSignatureSubmitPopup] = useState<boolean>(false);
  const { orderDetails } = useProviderNotificationData();
  const [signatureSigned, setSignatureSigned] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const orderColumns: Column[] = [{ id: "products", label: "Product(S)", minWidth: 100 }];

  const orderGraftRows: any[] = useMemo(() => {
    const rows: any[] = [];
    orderData?.orderItems.map((item: any, index: any) => {
      const row = {
        products: `${item.productItemDescription} (${item.quantity})`,
      };
      rows.push(row);
    });
    return rows;
  }, [orderData?.orderItems]);

  useEffect(() => {
    if (orderData?.orderId) {
      dispatch(getOrderDetailsForProvider(orderData?.orderId));
    }
  }, [orderData]);

  const handlePrescriptionPrview = async (orderId: string) => {
    const actionResult = await dispatch(previewPrescriptionPDF(parseInt(orderId)));
    const documentData = actionResult.payload;
    if (documentData && documentData.content) {
      setShowPDF({
        isShow: true,
        content: documentData.content,
        fileName: orderData?.orderNumber + "_Prescription",
      });
    }
  };

  const handleSignatureData = (data: any) => {
    const payload = {
      orderId: orderData?.orderId,
      providerId: orderDetails?.providerId,
      providerName: data?.providerInformation?.name,
      signature: data?.providerInformation?.signature,
      productId: orderDetails?.orderItems?.[0].productId || "",
    };
    dispatch(saveProviderSignAgainstOrder(payload))
      .then((response) => {
        if (response.payload && response.payload?.code === SUCCESS_CODE) {
          dispatch(
            setSingleAlertObj({
              alertType: "success",
              message: `Order (${orderData?.orderNumber})  signed and placed successfully.`,
            })
          );
          setSignatureSigned(true);
          onClose();
        }
      })
      .catch((error) => {
        dispatch(
          setSingleAlertObj({
            alertType: "error",
            message: "Something went wrong while signing the order!",
          })
        );
      });
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      {showPDF && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() => setShowPDF({ isShow: false, content: "", fileName: "" })}
          title={showPDF.fileName}
          fileName={showPDF.fileName}
          base64String={showPDF.content || ""}
        />
      )}
      {showSignatureSubmitPopup && (
        <SigantureSubmitPopup
          open={showSignatureSubmitPopup}
          onClose={() => {
            setShowSignatureSubmitPopup(false);
          }}
          addProductItem={() => {}}
          productItemDetails={{}}
          isEdit={false}
          practiceName={orderData?.practiceName}
          selectedProviderData={[
            { providerName: orderData?.orderInfo?.provider, npi: "", ptan: "", taxId: "" },
          ]}
          addSignature={handleSignatureData}
          authorizedSigner={false}
        />
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: paperSx(isMobile) }}
        onClick={(e) => e.stopPropagation()}
        keepMounted
        aria-describedby="sign-order-dialog-description"
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <FileSignature size={20} color={tokens.accent} strokeWidth={1.8} />
          </Box>
          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
              {signatureSigned ? `Order (${orderData?.orderNumber}) Signed` : `Sign Order (${orderData?.orderNumber})`}
            </Typography>
            <Typography id="sign-order-dialog-description" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              Please review the order details before signing. Click Preview to view the order, or Sign Order to complete and submit your
              signature.
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box sx={bodySx}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box>
                <FieldLabel label="Patient" tokens={tokens} />
                <Typography sx={{ fontSize: "0.95rem", color: tokens.textPrimary, fontWeight: 600 }}>{orderData?.patientName}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <FieldLabel label="Practice" tokens={tokens} />
                <Typography sx={{ fontSize: "0.95rem", color: tokens.textPrimary, fontWeight: 600 }}>{orderData?.practiceName}</Typography>
              </Box>
            </Grid>
          </Grid>

          <Box>
            <CustomTable
              columns={orderColumns}
              rows={orderGraftRows}
              disablePagination
              borderBox
              showMenu
              uniqueValue="Billing"
            />
          </Box>
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              flexWrap: "wrap",
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => {
                handlePrescriptionPrview(orderData?.orderId);
              }}
              sx={{
                ...backBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
                order: { xs: 1, sm: 1 },
              }}
            >
              <VisibilityOutlined sx={{ fontSize: "1.1rem" }} />
              Preview
            </Box>
            {!signatureSigned && (
              <Box
                component="button"
                type="button"
                onClick={() => setShowSignatureSubmitPopup(true)}
                sx={{
                  ...primaryBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "none",
                  cursor: "pointer",
                  color: "#fff",
                  order: { xs: 2, sm: 2 },
                }}
              >
                <DriveFileRenameOutline sx={{ fontSize: "1.1rem" }} />
                Sign Order
              </Box>
            )}
            <Box
              component="button"
              type="button"
              onClick={() => {
                onClose();
              }}
              sx={{
                ...cancelBtnSx,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.8,
                px: 3,
                py: 1.2,
                border: "1px solid",
                cursor: "pointer",
                order: { xs: 3, sm: 3 },
              }}
            >
              Cancel
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default SignOrderPopup;
